// DnDProvider — глобальный @dnd-kit контекст для «Хватай-и-в-коллекцию».
//
// Оборачивает всё приложение (рядом с layout в __root). Сенсоры:
//  • PointerSensor {distance:8} — desktop: tap по стретч-Link навигирует, drag
//    стартует только после смещения >8px (D5).
//  • TouchSensor {delay:250, tolerance:8} — mobile: long-press → drag, скролл/
//    swipe-back/swipe-actions живут (delay-порог + tolerance, без touch-action:
//    none) (D4). Разведение жестов со SwipeActions — см. draggable-shell.tsx.
//
// onDragStart → dragSession.start + haptic.impact medium (ТОЛЬКО mobile).
// onDragEnd → если over: handleDrop(item, targetSetId); затем dragSession.end.
// onDragCancel → dragSession.end. <DragOverlay> рисует «призрак» (reduced-motion
// уважается: без drop-анимации).
//
// Тактильный «тик» наведения (haptic.selection) НАМЕРЕННО живёт не здесь, а в
// CollectionChips (useHoverHaptic): срабатывает один раз на смену over-цели
// (isOver false→true конкретного чипа), а не на каждый pointermove — иначе на
// mobile была бы непрерывная вибрация во время перетаскивания.

import {
	type CollisionDetection,
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	type Modifier,
	MouseSensor,
	pointerWithin,
	rectIntersection,
	TouchSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { getEventCoordinates } from "@dnd-kit/utilities";
import { useHaptic } from "@studyhub/platform/hooks";
import type { ReactNode } from "react";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { IS_MOBILE_PLATFORM } from "@/lib/platform-detect";
import { type DragItem, useDragSessionStore } from "@/stores/drag-session";
import { DragOverlayCard } from "./drag-overlay-card";
import { useCollectionDrop } from "./use-collection-drop";

/**
 * Центрирует «призрак» под курсором/пальцем. Без него @dnd-kit якорит overlay к
 * левому-верхнему углу источника + дельта → компактная капсула уезжала далеко от
 * курсора (источник — большая карточка, overlay — маленькая капсула). Аналог
 * @dnd-kit/modifiers snapCenterToCursor, но без доп. зависимости.
 */
const snapCenterToCursor: Modifier = ({ activatorEvent, draggingNodeRect, transform }) => {
	if (!draggingNodeRect || !activatorEvent) return transform;
	const coords = getEventCoordinates(activatorEvent);
	if (!coords) return transform;
	const offsetX = coords.x - draggingNodeRect.left;
	const offsetY = coords.y - draggingNodeRect.top;
	return {
		...transform,
		x: transform.x + offsetX - draggingNodeRect.width / 2,
		y: transform.y + offsetY - draggingNodeRect.height / 2,
	};
};

const DRAG_OVERLAY_MODIFIERS: Modifier[] = [snapCenterToCursor];

/**
 * Коллизия по КУРСОРУ (pointerWithin), с фолбэком на rectIntersection. Дефолтный
 * rectIntersection сравнивал прямоугольник МАЛЕНЬКОГО overlay-«призрака» с целями —
 * над крупной плиткой коллекции часто не пересекалось → «держу поверх, а не
 * подсвечивается». pointerWithin подсвечивает цель прямо под курсором/пальцем.
 */
const dockCollisionDetection: CollisionDetection = (args) => {
	const pointer = pointerWithin(args);
	return pointer.length > 0 ? pointer : rectIntersection(args);
};

interface DnDProviderProps {
	children: ReactNode;
}

function extractDragItem(data: unknown): DragItem | null {
	if (!data || typeof data !== "object") return null;
	const item = (data as { item?: unknown }).item;
	if (!item || typeof item !== "object") return null;
	const { type, id, title } = item as Partial<DragItem>;
	if (typeof type !== "string" || typeof id !== "string" || typeof title !== "string") return null;
	return { type, id, title } as DragItem;
}

function extractSetId(data: unknown): string | null {
	if (!data || typeof data !== "object") return null;
	const setId = (data as { setId?: unknown }).setId;
	return typeof setId === "string" ? setId : null;
}

// Жёсткая блокировка скролла страницы на время активного drag (только touch).
// После long-press-захвата карточки палец должен дотащить её до Дока внизу, а не
// прокручивать список. @dnd-kit и так preventDefault'ит touchmove источника, но
// overflow:hidden + touch-action:none на <html> — страховка от остаточного скролла
// внешних контейнеров (фидбек: «заблокируй скролл, когда карточка схвачена»).
function setDragScrollLock(locked: boolean): void {
	if (!IS_MOBILE_PLATFORM) return;
	const el = document.documentElement;
	el.style.overflow = locked ? "hidden" : "";
	el.style.touchAction = locked ? "none" : "";
}

// Глобальный курсор `grabbing` на всё время активного drag (desktop). Курсор
// `grab` (рука «готова схватить») висит на самой карточке-источнике (DraggableShell);
// как только тащим — рука «сжимается» в кулак на ВСЁМ документе, чтобы при уходе
// курсора с карточки (а тащим по всей странице к Доку внизу) состояние не «дёргалось»
// обратно на pointer. setProperty с `important` перебивает любые cursor-* классы под
// курсором (стретч-Link карточек, кнопки, текст). Сброс — на end/cancel.
function setDragCursor(active: boolean): void {
	if (active) {
		document.body.style.setProperty("cursor", "grabbing", "important");
	} else {
		document.body.style.removeProperty("cursor");
	}
}

export function DnDProvider({ children }: DnDProviderProps): React.ReactElement {
	const haptic = useHaptic();
	const reducedMotion = usePrefersReducedMotion();
	const startDrag = useDragSessionStore((s) => s.start);
	const endDrag = useDragSessionStore((s) => s.end);
	const activeItem = useDragSessionStore((s) => s.item);
	const { handleDrop } = useCollectionDrop();

	// MouseSensor (НЕ PointerSensor!) для desktop + TouchSensor для touch.
	// PointerSensor реагирует и на mouse, и на touch pointer-события — на тач-
	// устройстве он хватал карточку при ЛЮБОМ движении >8px (= скролл списка, =
	// свайп по тёмному оверлею открытой шторки), полностью обходя long-press
	// TouchSensor (фидбек: «карточки тащатся просто при скролле» + «свайп по фону
	// за попапом тащит карточки»). MouseSensor игнорирует touch → на тач остаётся
	// ТОЛЬКО TouchSensor (long-press delay), а он после активации preventDefault'ит
	// touchmove → скролл во время drag заблокирован, карточку можно дотащить до Дока.
	const sensors = useSensors(
		useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
		// Touch: long-press (250ms, неподвижно в пределах 8px) → drag. Скролл/свайп
		// двигаются сразу → отменяют активацию до порога.
		useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
	);

	function onDragStart(event: DragStartEvent): void {
		const item = extractDragItem(event.active.data.current);
		if (!item) return;
		startDrag(item);
		setDragScrollLock(true);
		setDragCursor(true);
		// Тактильный «подхват» — только на mobile (desktop не вибрирует).
		if (IS_MOBILE_PLATFORM) haptic.impact({ style: "medium" });
	}

	function onDragEnd(event: DragEndEvent): void {
		const item = extractDragItem(event.active.data.current);
		const setId = event.over ? extractSetId(event.over.data.current) : null;
		if (item && setId) handleDrop(item, setId);
		setDragScrollLock(false);
		setDragCursor(false);
		endDrag();
	}

	function onDragCancel(): void {
		setDragScrollLock(false);
		setDragCursor(false);
		endDrag();
	}

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={dockCollisionDetection}
			// autoScroll off: док — fixed-цель снизу, прокрутка к ней не нужна; с
			// включённым autoScroll @dnd-kit прокручивал страницу при подведении к
			// нижнему краю (фидбек: «уезжает страница, когда тащу вниз»).
			autoScroll={false}
			onDragStart={onDragStart}
			onDragEnd={onDragEnd}
			onDragCancel={onDragCancel}
		>
			{children}
			<DragOverlay
				modifiers={DRAG_OVERLAY_MODIFIERS}
				dropAnimation={reducedMotion ? null : undefined}
			>
				{activeItem ? <DragOverlayCard item={activeItem} /> : null}
			</DragOverlay>
		</DndContext>
	);
}
